/**
 * Project categories.
 *
 * A project belongs to AT MOST ONE category, which is one of three things:
 *
 *   BUILT-IN   — one of {@link BUILT_IN_CATEGORIES}, stored as its stable id in
 *                `Project.category`. The id is the identity; the label is
 *                localized and must never be stored or compared as identity.
 *   CUSTOM     — a row the user created, referenced by `Project.customCategoryId`.
 *                A separate column precisely so a custom id can never be
 *                mistaken for a built-in id, or the reverse.
 *   UNASSIGNED — both columns null. This is the default for new projects.
 *
 * There is a fourth state that is not a category at all:
 *
 *   LEGACY     — `Project.category` holds a string this build does not
 *                recognise: a retired built-in (`other`, `animals`, …) or
 *                something older still. Those rows are READ verbatim and left
 *                alone. They are never shown as a selectable category, never
 *                remapped, and never rewritten unless the user explicitly picks
 *                something new.
 */

/** The built-in categories, in the exact order the UI must present them. */
export const BUILT_IN_CATEGORIES = [
  'characters',
  'movies',
  'shows',
  'comics',
  'games',
  'memes',
] as const;

export type BuiltInCategory = (typeof BUILT_IN_CATEGORIES)[number];

export function isBuiltInCategory(value: unknown): value is BuiltInCategory {
  return typeof value === 'string' && (BUILT_IN_CATEGORIES as readonly string[]).includes(value);
}

/**
 * Categories that USED to be built in.
 *
 * Listed only so legacy rows can be recognised as "a retired category" rather
 * than "corrupt data". Nothing here is selectable, translated, or shown as a
 * chip — and nothing here is ever rewritten. `other` in particular was the old
 * schema default, so a large share of existing production projects carry it.
 */
export const RETIRED_CATEGORIES = ['animals', 'emotions', 'love', 'food', 'other'] as const;

export function isRetiredCategory(value: unknown): boolean {
  return typeof value === 'string' && (RETIRED_CATEGORIES as readonly string[]).includes(value);
}

/**
 * True for a stored `category` value that this build cannot present.
 *
 * Both retired ids and anything unrecognised answer true — the product treats
 * them identically, which is what stops an unknown string from being special-
 * cased into a crash or a silent remap.
 */
export function isLegacyCategory(value: unknown): boolean {
  return typeof value === 'string' && value.length > 0 && !isBuiltInCategory(value);
}

// ---- Custom categories -----------------------------------------------------

/**
 * Longest custom category name.
 *
 * Chips sit in a single-line wrapping row next to six built-ins, so this is a
 * layout constraint as much as a data one — long enough for a real name in any
 * of the three languages, short enough that one chip cannot take a whole row.
 */
export const CUSTOM_CATEGORY_NAME_MAX = 24;

export interface CustomCategoryDTO {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * The comparison form of a category name.
 *
 * Case-insensitive and whitespace-insensitive, so `Games`, `games` and
 * `  GAMES  ` are one name — and so are `my category` and ` MY CATEGORY `.
 * Interior runs of whitespace collapse too, because "my  category" and
 * "my category" are the same name to a person.
 *
 * `toLowerCase` rather than `toLocaleLowerCase`: the result is a storage key
 * compared against other stored keys, and it must not change meaning with the
 * server's locale. Persian and Russian are unaffected either way.
 */
export function normalizeCategoryName(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

export type CategoryNameError =
  /** Nothing but whitespace. */
  | 'EMPTY'
  /** Longer than {@link CUSTOM_CATEGORY_NAME_MAX}. */
  | 'TOO_LONG'
  /** The user already has a category with this name. */
  | 'DUPLICATE'
  /** Collides with a built-in category's id or one of its localized labels. */
  | 'RESERVED';

export type CategoryNameResult =
  | { ok: true; name: string; normalized: string }
  | { ok: false; error: CategoryNameError };

export interface ValidateCategoryNameOptions {
  /** Normalized names this user already has. */
  existing?: Iterable<string>;
  /**
   * Normalized names that may not be reused — the built-in ids plus their
   * labels in every language.
   *
   * Passed IN rather than derived here: the labels live in
   * `@astral/localization`, which depends on this package, so reaching for them
   * from inside it would be a cycle. The caller that owns both supplies them.
   */
  reserved?: Iterable<string>;
}

/**
 * Validate a proposed custom category name.
 *
 * The trimmed, whitespace-collapsed name is returned with the USER'S OWN
 * casing preserved — only the comparison is case-insensitive. A user who types
 * "Sci-Fi" gets "Sci-Fi", not "sci-fi".
 */
export function validateCustomCategoryName(
  raw: string,
  options: ValidateCategoryNameOptions = {},
): CategoryNameResult {
  const name = raw.trim().replace(/\s+/g, ' ');
  if (name.length === 0) return { ok: false, error: 'EMPTY' };
  // Counted in CODE POINTS, so an emoji or a Persian cluster costs one, not two.
  if (Array.from(name).length > CUSTOM_CATEGORY_NAME_MAX) return { ok: false, error: 'TOO_LONG' };

  const normalized = normalizeCategoryName(name);
  // Reserved is checked BEFORE duplicate: "Games" collides with the built-in,
  // and saying so is more useful than "you already have this".
  if (options.reserved && setOf(options.reserved).has(normalized)) {
    return { ok: false, error: 'RESERVED' };
  }
  if (options.existing && setOf(options.existing).has(normalized)) {
    return { ok: false, error: 'DUPLICATE' };
  }
  return { ok: true, name, normalized };
}

function setOf(values: Iterable<string>): Set<string> {
  return values instanceof Set ? values : new Set(values);
}

// ---- Assignment ------------------------------------------------------------

/**
 * A project's category, resolved from the two stored columns.
 *
 * `legacy` carries the raw stored string so the UI can present it honestly
 * ("uncategorised") without the value being lost or rewritten behind the user.
 */
export type ProjectCategoryRef =
  | { kind: 'none' }
  | { kind: 'builtin'; id: BuiltInCategory }
  | { kind: 'custom'; id: string }
  | { kind: 'legacy'; value: string };

/**
 * Resolve the stored columns into a single reference.
 *
 * `customCategoryId` wins when both are somehow set. That cannot happen through
 * any write path — every assignment clears the other column — but a defensive
 * order beats an ambiguous one, and the custom row is the more specific claim.
 */
export function readProjectCategory(row: {
  category?: string | null;
  customCategoryId?: string | null;
}): ProjectCategoryRef {
  if (row.customCategoryId) return { kind: 'custom', id: row.customCategoryId };
  const value = row.category;
  if (!value) return { kind: 'none' };
  if (isBuiltInCategory(value)) return { kind: 'builtin', id: value };
  return { kind: 'legacy', value };
}

/**
 * The two column values for a chosen category.
 *
 * The invariant "at most one category" is enforced HERE, in one place: every
 * assignment writes both columns, so choosing one always clears the other and
 * the pair can never drift into holding two categories at once.
 */
export interface ProjectCategoryColumns {
  /** Built-in id, a legacy value, or null. */
  category: string | null;
  customCategoryId: string | null;
}

export function writeProjectCategory(ref: ProjectCategoryRef): ProjectCategoryColumns {
  switch (ref.kind) {
    case 'builtin':
      return { category: ref.id, customCategoryId: null };
    case 'custom':
      return { category: null, customCategoryId: ref.id };
    case 'legacy':
      return { category: ref.value, customCategoryId: null };
    default:
      return { category: null, customCategoryId: null };
  }
}
